import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import SyncIcon from '@atlaskit/icon-lab/core/sync';
import { Flex, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { getFeatureInfo } from '../../../common/getFeatureInfo';
import type { AudioFeature } from '../../../types/audioFeature';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';

export type AudioFeatureToggleProps = {
	featureList: AudioFeature[];
	onClick: () => void;
};

const i18n = defineMessages({
	switchToFeatureLabel: {
		id: 'ai-audio.player.feature-toggle.switch-to-feature-label',
		defaultMessage: 'Switch to',
		description: 'Label for the toggle button that allows users to switch to a different feature',
	},
});

const styles = cssMap({
	toggleButton: {
		height: '32px',
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
		paddingRight: token('space.150'),
		paddingLeft: token('space.150'),
		borderRadius: token('radius.full'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		color: token('color.text'),
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
});

export const AudioFeatureToggle = ({
	featureList,
	onClick,
}: AudioFeatureToggleProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { activeFeature } = useAudioPlayerStore();

	const activeFeatureInfo = getFeatureInfo(activeFeature, featureList);

	const getNextFeature = () => {
		const currentIndex = featureList.findIndex(
			(feature) => feature.name === activeFeatureInfo?.name,
		);
		const nextIndex = (currentIndex + 1) % featureList.length;
		return featureList[nextIndex];
	};

	const nextFeature = getNextFeature();

	return (
		<Pressable xcss={styles.toggleButton} onClick={onClick}>
			<Flex gap="space.150" alignItems="center">
				<Inline space="space.075" alignBlock="center">
					<SyncIcon label="" color={token('color.text.subtle')} />
					<Text size="medium" color="color.text.subtle">
						{formatMessage(i18n.switchToFeatureLabel)}{' '}
						{formatMessage(nextFeature.translationString)}
					</Text>
				</Inline>
			</Flex>
		</Pressable>
	);
};
